import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Listing } from 'components/shared/TableListing';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { InputField, CheckboxField } from 'components/form';

import { getLineItemRowStyles, getRemoveWithChildrenIndexes } from './lib';
import { DealSheetFormValues } from './types';
import { CustomerVisibility } from '../../enums';
import {
  LineItemCapsCell,
  LineItemTypeCell,
  LineItemAmountCell,
  LineItemActionsCell,
  LineItemVisibilityCell,
  CustomerVisibilityHeader,
} from './cells';

export const ChargesSection = () => {
  const { control } = useFormContext<DealSheetFormValues>();
  const { fields, append, remove, move } = useFieldArray({ control, name: 'charges' });

  const watched = useWatch({ control, name: 'charges' }) ?? [];
  const tableData = fields.map((field, index) => ({ ...field, ...(watched[index] ?? {}) }));

  const handleRemove = (rowIndex: number) => remove(getRemoveWithChildrenIndexes(rowIndex, tableData));

  return (
    <Listing
      tableData={tableData}
      columns={[
        {
          width: '16rem',
          renderKey: 'name',
          header: 'Charge/Fee Name',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => (
            <Stack gap="0.5rem" width="100%">
              <InputField
                control={control}
                label="Charge/Fee Name"
                name={`charges.${rowIndex}.name`}
                inputProps={{ variant: 'outlined' }}
              />
              <CheckboxField label="Taxable" control={control} name={`charges.${rowIndex}.isTaxable`} />
            </Stack>
          ),
        },
        {
          width: '12rem',
          renderKey: 'amountType',
          header: 'Type',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => <LineItemTypeCell rowIndex={rowIndex} />,
        },
        {
          width: '12rem',
          renderKey: 'name',
          header: 'Amount',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => <LineItemAmountCell rowIndex={rowIndex} section="charges" />,
        },
        {
          header: 'Description',
          renderKey: 'description',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => (
            <InputField
              control={control}
              label="Description"
              name={`charges.${rowIndex}.description`}
              inputProps={{ variant: 'outlined', multiline: true, minRows: 3 }}
            />
          ),
        },
        {
          width: '16rem',
          renderKey: 'caps',
          header: 'Min/Max caps',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => <LineItemCapsCell rowIndex={rowIndex} section="charges" />,
        },
        {
          width: '16rem',
          renderKey: 'customerVisibility',
          bodyStyles: getLineItemRowStyles,
          header: <CustomerVisibilityHeader />,
          renderCell: ({ rowIndex }) => <LineItemVisibilityCell rowIndex={rowIndex} section="charges" />,
        },
        {
          renderKey: 'name',
          header: 'Action',
          width: '10rem',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => (
            <LineItemActionsCell rowIndex={rowIndex} rowsCount={fields.length} onMove={move} onRemove={handleRemove} />
          ),
        },
      ]}
      actionPanelBottom={() => (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: '0.5rem' }}>
            Tip: use a negative amount to represent a discount (e.g. −$100 or −5%)
          </Typography>

          <Button
            size="small"
            onClick={() => {
              append({
                name: '',
                amount: 0,
                cap: null,
                id: uuidv4(),
                type: 'CHARGE',
                parentId: null,
                isTaxable: true,
                description: '',
                amountType: 'flat',
                isEquipmentLvl: false,
                sortOrder: fields.length,
                customerVisibility: CustomerVisibility.Visible,
              });
            }}
          >
            Add Charge / Discount
          </Button>
        </Box>
      )}
    />
  );
};
